import { useLocation, useNavigate } from "@remix-run/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Modal } from "react-daisyui";
import { useTranslation } from "react-i18next";
import { SwiperOptions } from "swiper/types";

import { IRelatedPropertiesModalProps } from "./types";
import RelatedPropertiesMobileModal from "../RelatedPropertiesMobileModal";

import { Icon } from "~/components/atoms/Icons";
import IconChevronRight from "~/components/atoms/Icons/IconChevronRight";
import IconClose from "~/components/atoms/Icons/IconClose";
import { SwiperSlide } from "~/components/atoms/Swiper";
import Swiper, { ForwardedSwiper } from "~/components/atoms/Swiper/Swiper";
import Typo from "~/components/atoms/Typo";
import CardProject from "~/components/molecules/CardProject";
import { cn } from "~/utilities/cn";
import { EIcon } from "~/utilities/enums/Icons";
import { Slug } from "~/utilities/enums/Slug";
import { ETypoVariant } from "~/utilities/enums/Typo";
const RelatedPropertiesModal: React.FC<IRelatedPropertiesModalProps> = ({
  relatedProjects,
  setRelatedProjectId,
  open,
  onClose,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeIndex, setActiveIndex] = useState<number>(0);

  useEffect(() => {
    const close = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    if (open) {
      window.addEventListener("keydown", close);
    } else {
      window.removeEventListener("keydown", close);
    }
    return () => window.removeEventListener("keydown", close);
  }, [onClose, open]);

  const options: SwiperOptions = useMemo(
    () => ({
      breakpoints: {
        0: {
          slidesPerView: 1,
          spaceBetween: 24,
        },
        769: {
          slidesPerView: 2,
          spaceBetween: 28,
        },
        1281: {
          slidesPerView: 2,
          spaceBetween: 32,
        },
        1440: {
          slidesPerView: 2.5,
          spaceBetween: 24,
        },
        1680: {
          slidesPerView: 2.5,
          spaceBetween: 24,
        },
        1920: {
          slidesPerView: 2.5,
          spaceBetween: 24,
        },
        2560: {
          slidesPerView: 2.5,
          spaceBetween: 24,
        },
      },
      containerModifierClass: "slides-related-properties-container",
      observeParents: true,
      observer: true,
      centerInsufficientSlides: true,
      effect: "slide",
      on: {
        slideChange: (swiper) => {
          setActiveIndex(swiper.activeIndex);
        },
      },
    }),
    [relatedProjects.length]
  );

  const swiperRef = useRef<ForwardedSwiper | null>(null);

  const handleMoreProjectsClick = useCallback(() => {
    onClose();
    if (location.pathname !== Slug.PROJECTS) {
      navigate(Slug.PROJECTS);
    }
  }, [onClose, location.pathname, navigate]);

  const handlePrevSlide = useCallback(() => {
    swiperRef?.current?.core?.swiper?.slidePrev();
  }, []);

  const handleNextSlide = useCallback(() => {
    swiperRef?.current?.core?.swiper?.slideNext();
  }, []);

  return (
    <>
      <Modal.Legacy
        open={open}
        className={cn(
          "block w-11/12 max-w-[864px] !bg-background px-8 lg:hidden lg:max-h-[90dvh] lg:max-w-full"
        )}
        onClickBackdrop={onClose}
      >
        <Modal.Header className="flex w-full flex-row items-start justify-between">
          <div className="flex flex-col gap-1">
            <Typo
              variant={ETypoVariant.HEADER_32}
              tag="h4"
              className="font-bold"
            >
              {t("related_properties.title")}
            </Typo>
            <Typo variant={ETypoVariant.HEADER_16} tag="p">
              {t("related_properties.some_properties_interested_in")}
            </Typo>
          </div>
          <IconClose onClick={onClose} />
        </Modal.Header>
        <Modal.Body>
          <div className="overflow-x-auto overflow-y-hidden">
            <div className="relative z-10 -my-10 flex flex-col gap-y-8 lg:gap-y-5">
              <div className="relative ">
                <Swiper defaultValue={activeIndex} ref={swiperRef} {...options}>
                  {relatedProjects?.map((project) => (
                    <SwiperSlide
                      className="h-auto   rounded-3xl py-10"
                      key={project.id}
                    >
                      <CardProject
                        project={project}
                        className=" h-full overflow-hidden rounded-3xl "
                        isShownCompareButton={false}
                        handleOnSelectProject={() => {
                          setRelatedProjectId(project.id);
                        }}
                      />
                    </SwiperSlide>
                  ))}
                </Swiper>
                {relatedProjects?.length > 2 && (
                  <>
                    <button
                      onClick={handlePrevSlide}
                      className={cn(
                        "absolute left-2 top-1/2 z-30 flex -translate-y-1/2 items-center justify-center rounded-full bg-color px-5 py-2 shadow-md transition-transform duration-200 hover:bg-color lg:bottom-3 lg:left-auto lg:right-[88px] lg:top-auto lg:-translate-y-0 lg:px-3 lg:py-1",
                        {
                          "cursor-not-allowed opacity-50": activeIndex === 0,
                          "hover:scale-105 active:scale-95": activeIndex !== 0,
                        }
                      )}
                      disabled={activeIndex === 0}
                    >
                      <Icon
                        TypeIcon={EIcon.ArrowLeftIcon}
                        height={40}
                        width={20}
                        className="text-backgroundPageProject"
                      />
                    </button>
                    <button
                      onClick={handleNextSlide}
                      className={cn(
                        "absolute right-2 top-1/2 z-30 flex -translate-y-1/2 items-center justify-center rounded-full bg-color px-5 py-2 shadow-md transition-transform duration-200 hover:bg-color lg:bottom-3 lg:right-[32px] lg:top-auto lg:-translate-y-0 lg:px-3 lg:py-1",
                        {
                          "cursor-not-allowed opacity-50":
                            activeIndex === relatedProjects.length - 2,
                          "hover:scale-105 active:scale-95":
                            activeIndex !== relatedProjects.length - 2,
                        }
                      )}
                      disabled={activeIndex === relatedProjects.length - 2}
                    >
                      <Icon
                        TypeIcon={EIcon.ArrowRightIcon}
                        height={40}
                        width={20}
                        className="text-backgroundPageProject"
                      />
                    </button>
                  </>
                )}
              </div>
            </div>
            <div className="relative z-20 mt-4 flex items-center justify-center">
              <button
                className="flex w-fit cursor-pointer items-center gap-1.5"
                onClick={handleMoreProjectsClick}
              >
                <Typo
                  variant={ETypoVariant.HEADER_16}
                  tag="p"
                  className="font-bold"
                >
                  {t("related_properties.more_project_available")}
                </Typo>
                <IconChevronRight />
              </button>
            </div>
          </div>
        </Modal.Body>
      </Modal.Legacy>
      <div className="hidden lg:block ">
        <RelatedPropertiesMobileModal
          projects={relatedProjects}
          open={open}
          onClose={onClose}
        />
        ;
      </div>
    </>
  );
};

export default RelatedPropertiesModal;
